import { db } from '@/models/db';
import type { DeltaPacketRecord } from '@/models/hydrate.ts';
import { ConfirmationHistory } from '@/models/registry.ts';
import type { BaseStore } from '@/models/stores/base.ts';
import type { ConfirmationHistoryEntry as ConfirmationHistoryType } from '@/models/types.ts';
import { makeAutoObservable, observable, type ObservableMap } from 'mobx';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import { confirmationsStore } from './confirmations';
import { dealStore } from './deals';

class ConfirmationHistoryStore
  implements BaseStore<ConfirmationHistory, ConfirmationHistoryType>
{
  store: ObservableMap<string, ConfirmationHistory> = observable.map();

  // index: confirmation_id -> array representing confirmation history
  storeIndexedByForeignKey: ObservableMap<string, ConfirmationHistory[]> =
    observable.map();

  constructor() {
    makeAutoObservable(
      this,
      {
        store: observable.shallow,
        storeIndexedByForeignKey: observable.shallow,
      },
      { autoBind: true },
    );
  }

  private getOrCreateCollection(confirmationId: string): ConfirmationHistory[] {
    let arr = this.storeIndexedByForeignKey.get(confirmationId);
    if (!arr) {
      arr = observable.array<ConfirmationHistory>([], { deep: false });
      this.storeIndexedByForeignKey.set(confirmationId, arr);
    }
    return arr;
  }

  private setHistoryEntryField = <K extends keyof ConfirmationHistoryType>(
    entry: ConfirmationHistory,
    key: K,
    value: ConfirmationHistoryType[K],
  ) => {
    (entry as Pick<ConfirmationHistory, K>)[key] =
      value as ConfirmationHistory[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<ConfirmationHistoryType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.confirmation_history_entries.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.confirmation_history_entries.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.confirmation_history_entries.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<ConfirmationHistoryType>;
  }) {
    const existingConfirmationHistory = this.getById(delta.record_id);
    if (!existingConfirmationHistory) return;

    const entries = Object.entries(delta.data) as [
      keyof ConfirmationHistoryType,
      ConfirmationHistoryType[keyof ConfirmationHistoryType],
    ][];

    for (const [key, newValue] of entries) {
      if (
        newValue === undefined ||
        existingConfirmationHistory[key] === newValue
      ) {
        continue;
      }

      this.setHistoryEntryField(existingConfirmationHistory, key, newValue);
    }
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<ConfirmationHistoryType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<ConfirmationHistoryType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.confirmation_history_entries.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.store.delete(delta.record_id);
      return;
    }

    if (delta.action === 'archive') {
      this.store.delete(delta.record_id);
      db.confirmation_history_entries.delete(delta.record_id);
      return;
    }

    this.add(delta.data);
  }

  getById(id: string): ConfirmationHistory | undefined {
    return this.store.get(id);
  }

  add(input: ConfirmationHistoryType) {
    const entry = new ConfirmationHistory(input);
    this.store.set(entry.id, entry);
    this.getOrCreateCollection(entry.confirmation_id).push(entry);
  }

  upsert(entry: ConfirmationHistoryType) {
    const existingEntry = this.getById(entry.id);
    if (existingEntry) {
      return; // append-only history, replacing existing entries shouldn't happen
    }

    this.add(entry);
  }

  async hydrateLocal(): Promise<void> {
    await hydrateFromWorker({
      tableName: 'confirmation_history_entries',
      entity: 'confirmation_history_entries',
      modelStore: confirmationHistoryStore,
    });
  }

  async hydrateDeal(
    confirmationIds: ConfirmationHistoryType['confirmation_id'][],
  ): Promise<void> {
    const items = await db.confirmation_history_entries.toArray();
    items.forEach((item) => {
      if (confirmationIds.includes(item.confirmation_id)) this.upsert(item);
    });
  }

  async hydrateOnlyForNewDeals(): Promise<void> {
    const newDeals = dealStore.newDeals;

    const confirmationIds: string[] = [];
    for (const deal of newDeals) {
      const confirmations = confirmationsStore.storeIndexedByDealId.get(
        deal.id,
      );
      if (confirmations) {
        confirmations.forEach((confirmation) =>
          confirmationIds.push(confirmation.id),
        );
      }
    }

    await hydrateFromWorker({
      tableName: 'confirmation_history_entries',
      entity: 'confirmation_history_entries',
      modelStore: confirmationHistoryStore,
      ids: confirmationIds,
    });
  }
}

export const confirmationHistoryStore = new ConfirmationHistoryStore();
